/**
 * DMS events: the admin listing without members, multipart creation / update
 * carrying the picture, archiving.
 */
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@/shared/api/client';
import { getToken } from '@/features/auth/session';

export const dmsEventSchema = z.object({
  id: z.string(),
  name: z.string().nullish(),
  type: z.string().nullish(),
  location: z.string().nullish(),
  storeCode: z.string().nullish(),
  eventId: z.string().nullish(),
  startDate: z.string().nullish(),
  endDate: z.string().nullish(),
  picture: z.string().nullish(),
});

export type DmsEvent = z.infer<typeof dmsEventSchema>;

export const dmsEventsQueryKey = ['dms-events'] as const;

/** GET dms-event/light: every event, members left out. */
export const dmsEventsQueryOptions = () =>
  queryOptions({
    queryKey: dmsEventsQueryKey,
    queryFn: () =>
      api.get<DmsEvent[]>('dms-event/light', {
        schema: z.array(dmsEventSchema),
      }),
    retry: false,
  });

/** The fields of an event sent by the dialog. */
export interface EventInput {
  name: string;
  location: string;
  eventId: string;
  storeCode: string;
  type: string;
  startDate: string | null;
  endDate: string | null;
  /** The stored or default picture path, only when no file was chosen. */
  picture?: string;
}

/** Every defined field, plus the picture file when one was chosen. */
const toFormData = (event: EventInput, picture: File | null): FormData => {
  const formData = new FormData();
  Object.entries(event).forEach(([key, value]) => {
    if (value !== null && value !== undefined)
      formData.append(key, String(value));
  });
  if (picture) formData.append('picture', picture);
  return formData;
};

/** The dialog only waits for the answer: its content is not read. */
const sendMultipart = async (
  path: string,
  method: 'POST' | 'PUT',
  body: FormData,
): Promise<void> => {
  const headers = new Headers({ Accept: 'application/json' });
  const token = getToken();
  if (token) headers.set('Authorization', token);
  const response = await fetch(`/api/${path}`, {
    method,
    headers,
    body,
    credentials: 'same-origin',
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Request failed with status ${response.status}`);
  }
};

export const createDmsEvent = (event: EventInput, picture: File | null) =>
  sendMultipart('dms-event', 'POST', toFormData(event, picture));

export const updateDmsEvent = (
  id: string,
  event: EventInput,
  picture: File | null,
) =>
  sendMultipart(
    `dms-event/${encodeURIComponent(id)}`,
    'PUT',
    toFormData(event, picture),
  );

export const archiveDmsEvent = (id: string) =>
  api.put<unknown>(`dms-event/${encodeURIComponent(id)}/archive`, {});

export const deleteDmsEvent = (id: string) =>
  api.delete<unknown>(`dms-event/${encodeURIComponent(id)}`);
